// Task #3694 — DTO risposta condivisi per gli endpoint consumati da POSSettings.tsx.
//
// Contratto dati BE↔FE: schemi Zod di RISPOSTA. Tipi derivati con `z.infer`.
// Fedeltà al WIRE reale (JSON servito dal backend):
//  - pg numeric(x,y) e COUNT/SUM senza `::int` → stringa sul wire → z.union([z.string(), z.number()]);
//  - COUNT ...::int / ROUND(...)::int → z.number();
//  - timestamp/timestamptz → z.union([z.string(), z.date()]) (wire = ISO string;
//    Date per riuso BE pre-serializzazione) — stessa convenzione di crm.ts;
//  - colonne NULLABLE nello schema DB o alias da LEFT JOIN → .nullable();
//  - jsonb generici → z.unknown();
//  - righe da `SELECT alias.*` → enumerate le colonne note dallo schema DB
//    (packages/database/db/schema.sql) chiuse con `.passthrough()`.
//
// Envelope wire (non modellato qui, gestito da apiClient):
//   successResponse   → { success:true, data:<value>, message? }
//   paginatedResponse → { success:true, data:<items>, pagination:{page,limit,total,totalPages} }

import { z } from 'zod';

/** timestamp/timestamptz: ISO string sul wire, Date lato BE pre-serializzazione. */
const wireTimestamp = z.union([z.string(), z.date()]);
/** pg numeric(x,y) o COUNT/SUM senza `::int`: stringa sul wire (driver pg), talvolta number. */
const wireNumeric = z.union([z.string(), z.number()]);

// ============================================================================
// 1) GET /api/pricelists — pos-pricelist.service.listPriceLists (riga ~229)
//    SELECT pl.*, (pl.managed_by_org_id IS NOT NULL) AS is_locked,
//           s.name as supplier_name,
//           (SELECT COUNT(*) ... pli)::int as items_count
//    pl.* = tutte le colonne pos_price_lists (enumerate) + campi calcolati.
// ============================================================================
// NB: naming `Summary` per non collidere con `PosPriceListRow` di ./pos/pricelist
// (riga listino per-prodotto, concetto diverso) — entrambi ri-esportati dal barrel.
export const posPriceListSummaryRowSchema = z
  .object({
    id: z.string(),
    tenant_id: z.string(),
    name: z.string(),
    description: z.string().nullable(), // DB nullable
    is_default: z.boolean(),
    valid_from: z.union([z.string(), z.date()]).nullable(), // pg date, nullable
    valid_to: z.union([z.string(), z.date()]).nullable(),
    created_at: wireTimestamp,
    updated_at: wireTimestamp,
    supplier_id: z.string(),
    list_type: z.string(), // CHECK: standard|canvas|device_promo|bundle
    archived_at: wireTimestamp.nullable(),
    group_id: z.string(),
    version: z.number(),
    is_current_version: z.boolean(),
    retail_brand_id: z.string().nullable(),
    managed_by_org_id: z.string().nullable(),
    customer_scope: z.string(), // CHECK: consumer|business|mixed
    priority: z.number(),
    default_vat_rate_id: z.string(),
    default_entry_fee_vat_rate_id: z.string().nullable(),
    default_sdi_rif_id: z.number().nullable(), // smallint, nullable
    default_entry_fee_sdi_rif_id: z.number().nullable(),
    // Campi calcolati dalla SELECT:
    is_locked: z.boolean(), // (managed_by_org_id IS NOT NULL) → boolean sul wire
    supplier_name: z.string().nullable(), // LEFT JOIN stores? no: s.name su supplier → nullable
    items_count: z.number(), // COUNT(*)::int → number
  })
  .passthrough(); // tollera colonne pos_price_lists future
export type PosPriceListSummaryRow = z.infer<typeof posPriceListSummaryRowSchema>;

// 2) GET /api/pos/fiscal-departments → `posFiscalDepartmentRowSchema` in ./fiscal-departments

// ============================================================================
// 3) GET /api/pos/fiscal-printers — pos-fiscal-printer.service.listPrinters
//    SELECT fp.*, s.name as store_name,
//           (SELECT COUNT(*) ... status='error') as error_count,
//           (SELECT COUNT(*) ... status IN ('pending','retry')) as pending_count
//    LEFT JOIN stores s ...
//    fp.* = tutte le colonne pos_fiscal_printers (enumerate).
// ============================================================================
export const posFiscalPrinterRowSchema = z
  .object({
    id: z.string(),
    tenant_id: z.string(),
    name: z.string(),
    brand: z.string(), // CHECK: epson|custom|rch
    model: z.string().nullable(), // DEFAULT '' ma può risultare null da alcune sorgenti
    serial_number: z.string(),
    factory_number: z.string().nullable(),
    connection_type: z.string(), // CHECK: websocket|agent
    ip_address: z.string().nullable(),
    port: z.number().nullable(), // integer DEFAULT 9100, nullable
    endpoint_path: z.string().nullable(),
    agent_id: z.string().nullable(),
    warehouse_id: z.string().nullable(),
    register_name: z.string().nullable(),
    receipt_header: z.string().nullable(),
    receipt_footer: z.string().nullable(),
    enabled_features: z.unknown(), // jsonb generico
    status: z.string(), // CHECK: active|maintenance|disabled
    last_heartbeat: wireTimestamp.nullable(),
    last_z_number: z.number().nullable(),
    last_z_date: wireTimestamp.nullable(),
    firmware_version: z.string().nullable(),
    created_at: wireTimestamp,
    updated_at: wireTimestamp,
    store_id: z.string(),
    is_default: z.boolean(),
    provider_kind: z.string().nullable(), // DEFAULT 'physical', colonna nullable
    last_department_push: z.unknown().nullable(), // jsonb, nullable
    receipt_extra_lines: z.unknown(), // jsonb generico
    receipt_copies: z.number().nullable(),
    auto_print: z.boolean().nullable(),
    receipt_header_print_digital: z.boolean().nullable(),
    receipt_footer_print_digital: z.boolean().nullable(),
    receipt_footer_qr_url: z.string().nullable(),
    // Campi calcolati dalla SELECT:
    store_name: z.string().nullable(), // LEFT JOIN stores → nullable
    error_count: wireNumeric, // COUNT(*) senza ::int → stringa sul wire
    pending_count: wireNumeric,
  })
  .passthrough(); // tollera colonne pos_fiscal_printers future
export type PosFiscalPrinterRow = z.infer<typeof posFiscalPrinterRowSchema>;

// ============================================================================
// 4) GET /api/pos/fiscal-agents — pos-fiscal-printer.service.listAgents
//    SELECT *, (last_seen ... freshness) AS is_online,
//           90 AS online_freshness_sec
//    FROM pos_fiscal_agents ...
//    Nota: per utenti non-admin la route azzera `auth_token` (→ undefined,
//    quindi il campo può mancare dal JSON) — vedi route :5801.
// ============================================================================
export const posFiscalAgentRowSchema = z
  .object({
    id: z.string(),
    tenant_id: z.string(),
    name: z.string(),
    // auth_token: NOT NULL su DB ma rimosso (undefined) per non-admin → optional.
    auth_token: z.string().optional(),
    status: z.string(), // CHECK: online|offline
    last_seen: wireTimestamp.nullable(),
    ip_address: z.string().nullable(),
    agent_version: z.string().nullable(),
    created_at: wireTimestamp,
    updated_at: wireTimestamp,
    uptime_seconds: z.number().nullable(),
    commands_executed: z.number().nullable(),
    errors_count: z.number().nullable(),
    memory_usage_mb: z.number().nullable(),
    os_platform: z.string().nullable(),
    os_arch: z.string().nullable(),
    os_hostname: z.string().nullable(),
    os_version: z.string().nullable(),
    reachable_printers: z.unknown(), // jsonb DEFAULT '[]'
    // Campi calcolati dalla SELECT:
    is_online: z.boolean(), // espressione booleana su last_seen → boolean
    online_freshness_sec: z.number(), // literal int (90) → number
  })
  .passthrough(); // tollera colonne pos_fiscal_agents future
export type PosFiscalAgentRow = z.infer<typeof posFiscalAgentRowSchema>;

// ============================================================================
// 5) GET /api/stores — pos-warehouse.routes :824
//    SELECT stores.id, stores.name,
//           (SELECT sb.retail_code ... LIMIT 1) AS store_id, stores.status
//    + warehouses[] (SELECT id,code,name,is_default FROM pos_warehouses)
//    + fiscal_printers[] (SELECT id,name,brand,model,serial_number,is_default,status)
// ============================================================================
export const posStoreWarehouseSchema = z.object({
  id: z.string(),
  code: z.string(),
  name: z.string(),
  is_default: z.boolean(),
});
export type PosStoreWarehouse = z.infer<typeof posStoreWarehouseSchema>;

export const posStoreFiscalPrinterSchema = z.object({
  id: z.string(),
  name: z.string(),
  brand: z.string(),
  model: z.string().nullable(), // DEFAULT '' ma nullable
  serial_number: z.string().nullable(),
  is_default: z.boolean(),
  status: z.string(),
});
export type PosStoreFiscalPrinter = z.infer<typeof posStoreFiscalPrinterSchema>;

export const posStoreWithDevicesSchema = z.object({
  id: z.string(),
  name: z.string(),
  store_id: z.string().nullable(), // sb.retail_code da subquery → nullable
  status: z.string(),
  warehouses: z.array(posStoreWarehouseSchema),
  fiscal_printers: z.array(posStoreFiscalPrinterSchema),
});
export type PosStoreWithDevices = z.infer<typeof posStoreWithDevicesSchema>;

// ============================================================================
// 6) GET /api/pos/printers/health — pos-warehouse.routes :6218
//    Mapper esplicito (righe 6247-6256): campi normalizzati (non `.*`).
// ============================================================================
export const posPrinterHealthSchema = z.object({
  id: z.string(),
  name: z.string(),
  brand: z.string(),
  provider_kind: z.string(), // mapper: p.provider_kind || 'physical' → sempre string
  status: z.enum(['online', 'warning', 'offline']), // healthStatus calcolato dal mapper
  error_count: z.number(), // mapper: p.error_count || 0 → number
  pending_count: z.number(), // mapper: p.pending_count || 0 → number
  last_heartbeat: wireTimestamp.nullable(), // timestamptz o null
});
export type PosPrinterHealth = z.infer<typeof posPrinterHealthSchema>;

// ============================================================================
// 7) GET /api/pos/printers/:id/status — pos-warehouse.routes :6263
//    Due varianti wire: A-Cube (provider_kind:'acube') e fisica ('physical').
//    Modellato lo shape richiesto dal task (printer nullable + flag) tollerando
//    i campi extra di ciascun ramo via `.passthrough()`.
// ============================================================================
export const posPrinterRtStatusPrinterSchema = z
  .object({
    id: z.string(),
    name: z.string(),
    // Campi presenti nel ramo fisico (nullabilità = schema pos_fiscal_printers):
    brand: z.string().optional(),
    model: z.string().nullable().optional(),
    serial_number: z.string().nullable().optional(),
    status: z.string().optional(),
    last_heartbeat: wireTimestamp.nullable().optional(),
    last_z_number: z.number().nullable().optional(),
    last_z_date: wireTimestamp.nullable().optional(),
    enabled_features: z.unknown().optional(), // jsonb
    connection_type: z.string().optional(),
    ip_address: z.string().nullable().optional(),
    port: z.number().nullable().optional(),
    endpoint_path: z.string().nullable().optional(),
    firmware_version: z.string().nullable().optional(),
    // Campo presente nel ramo A-Cube:
  })
  .passthrough();
export type PosPrinterRtStatusPrinter = z.infer<typeof posPrinterRtStatusPrinterSchema>;

export const posPrinterRtStatusSchema = z
  .object({
    // `printer` nullable: ramo "nessuna stampante selezionata" ritorna null.
    printer: posPrinterRtStatusPrinterSchema.nullable(),
    isReachable: z.boolean().optional(),
    agentOnline: z.boolean().optional(),
    errorCount: wireNumeric.optional(), // COUNT/SUM: stringa o number sul wire
    configured: z.boolean().optional(), // false quando nessuna stampante
    provider_kind: z.string().optional(), // 'acube' | 'physical' a seconda del ramo
  })
  .passthrough(); // tollera i campi specifici dei rami acube/physical
export type PosPrinterRtStatus = z.infer<typeof posPrinterRtStatusSchema>;

// ============================================================================
// 8) GET /api/pos/printers/:id/history — pos-warehouse.routes :6384
//    data = { printer, filters, total, items[] }.
//    items = UNION ALL webhook (acube_webhook_events) + queue (pos_fiscal_print_queue),
//    colonne allineate: i campi non pertinenti a un ramo sono
//    NULL::<tipo> → nullable sul wire.
// ============================================================================
export const posPrinterRtHistoryItemSchema = z
  .object({
    id: z.string(), // 'wh:<uuid>' | 'q:<uuid>'
    type: z.string(), // 'webhook' | 'command' | 'error'
    timestamp: wireTimestamp, // received_at | created_at
    title: z.string(), // COALESCE(...) → sempre valorizzato
    status: z.string().nullable(), // stato coda | dispatch_status nel ramo webhook
    attempt_count: z.number().nullable(), // NULL::int nel ramo webhook
    max_attempts: z.number().nullable(),
    error_code: z.string().nullable(),
    error_message: z.string().nullable(),
    processed_at: wireTimestamp.nullable(), // processed_at coda | dispatched_at webhook
    resource_id: z.string().nullable(), // NULL::text nel ramo queue
    signature_valid: z.boolean().nullable(), // NULL::boolean nel ramo queue
    payload: z.unknown().nullable(), // jsonb, NULL::jsonb nel ramo queue
  })
  .passthrough();
export type PosPrinterRtHistoryItem = z.infer<typeof posPrinterRtHistoryItemSchema>;

export const posPrinterRtHistorySchema = z.object({
  printer: z.object({
    id: z.string(),
    name: z.string(),
    provider_kind: z.string().nullable(), // colonna pos_fiscal_printers nullable
  }),
  filters: z.object({
    event_type: z.string(), // all|webhook|error|command
    from: z.string().nullable(), // ISO string o null (normalizzato dalla route)
    to: z.string().nullable(),
    limit: z.number(),
    offset: z.number(),
  }),
  total: z.number(), // COUNT(*)::int → number
  items: z.array(posPrinterRtHistoryItemSchema),
});
export type PosPrinterRtHistory = z.infer<typeof posPrinterRtHistorySchema>;

// ============================================================================
// 9) GET /api/pos/fiscal-queue — pos-fiscal-printer.service.listQueue (riga ~868)
//    paginatedResponse: data = items[], pagination = {page,limit,total,totalPages}.
//    SELECT q.*, fp.name as printer_name, fp.brand as printer_brand
//    LEFT JOIN pos_fiscal_printers fp ...
//    q.* = tutte le colonne pos_fiscal_print_queue (enumerate).
// ============================================================================
export const posFiscalQueueItemSchema = z
  .object({
    id: z.string(),
    tenant_id: z.string(),
    fiscal_printer_id: z.string(),
    command_type: z.string(), // CHECK: receipt|refund|void|z_report|...
    payload: z.unknown(), // jsonb generico
    status: z.string(), // CHECK: pending|sending|success|error|retry|cancelled|expired
    attempt_count: z.number(),
    max_attempts: z.number(),
    error_code: z.string().nullable(),
    error_message: z.string().nullable(),
    printer_response: z.unknown().nullable(), // jsonb, nullable
    sale_id: z.string().nullable(),
    fiscal_document_id: z.string().nullable(),
    created_at: wireTimestamp,
    processed_at: wireTimestamp.nullable(),
    created_by: z.string().nullable(),
    // Alias da LEFT JOIN pos_fiscal_printers → nullable:
    printer_name: z.string().nullable(),
    printer_brand: z.string().nullable(),
  })
  .passthrough(); // tollera colonne pos_fiscal_print_queue future
export type PosFiscalQueueItem = z.infer<typeof posFiscalQueueItemSchema>;
